import { BaseApiService } from './BaseApiService'
import {
  LoginResponse,
  AccountBalanceResponse,
  AccountResponse,
  ReferralStatsResponse,
  CallHistoryResponse,
  TopUpHistoryResponse,
  AccountContactsResponse,
  ContactLabel,
  GoogleContactsSyncResponse,
  PhoneVerificationTriggerResponse,
  VerificationStatusResponse,
  AllowedVerificationCountriesResponse,
  ApiKeyListResponse,
  CreateApiKeyResponse,
  AccountSettingsResponse,
  AccountSettings,
  AgentVoicesResponse,
} from '@/types/account'

/**
 * Account API Service
 *
 * Handles API calls related to account management,
 * including authentication, balance, and top-ups
 */
export class AccountApiService extends BaseApiService {
  async googleSignIn(idToken: string, referredBy?: string): Promise<LoginResponse> {
    const timezone = typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined
    const locale = typeof navigator !== 'undefined' ? navigator.language : undefined

    const result = await this.post<LoginResponse>('/google/login', {
      idToken,
      referredBy,
      timezone,
      locale,
    })

    if (result.token && result.expiresAt && result.refreshToken) {
      this.setCredentials({
        token: result.token,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
        refreshTokenExpiresAt: result.refreshTokenExpiresAt,
      })
    }
    return result
  }

  async appleSignIn(idToken: string, referredBy?: string): Promise<LoginResponse> {
    const result = await this.post<LoginResponse>('/apple/login', { idToken, referredBy })

    if (result.token && result.expiresAt && result.refreshToken) {
      this.setCredentials({
        token: result.token,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
        refreshTokenExpiresAt: result.refreshTokenExpiresAt,
      })
    }
    return result
  }

  /**
   * Sign out (clear local session)
   */
  async signOut(): Promise<void> {
    try {
      await this.post<{ success: boolean }>('/auth/logout', {}, {
        requiresAuth: true
      })
    } finally {
      // Clear stored credentials regardless of API response
      this.clearCredentials()
    }
  }

  /**
   * Get account details for the authenticated account
   */
  async getAccount(): Promise<AccountResponse> {
    return this.get<AccountResponse>('/account/me', {
      requiresAuth: true
    })
  }

  async getReferrals(): Promise<ReferralStatsResponse> {
    return this.get<ReferralStatsResponse>('/account/referrals', {
      requiresAuth: true,
    })
  }

  /**
   * Get account balance for the authenticated account
   */
  async getAccountBalance(): Promise<AccountBalanceResponse> {
    return this.get<AccountBalanceResponse>('/account/balance', {
      requiresAuth: true
    })
  }

  async getCallHistory(page = 1, pageSize = 10, query?: string): Promise<CallHistoryResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    if (query) params.set('query', query)
    return this.get<CallHistoryResponse>(`/account/call-history?${params.toString()}`, {
      requiresAuth: true
    })
  }

  async getTopUpHistory(page = 1, pageSize = 10): Promise<TopUpHistoryResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<TopUpHistoryResponse>(`/account/transactions?${params.toString()}`, {
      requiresAuth: true
    })
  }

  async getContacts(page = 1, pageSize = 5, label?: ContactLabel, query?: string): Promise<AccountContactsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    if (label !== undefined) params.set('label', label)
    if (query) params.set('query', query)
    return this.get<AccountContactsResponse>(`/account/contacts?${params.toString()}`, {
      requiresAuth: true
    })
  }

  async getContactLabels(): Promise<ContactLabel[]> {
    const res = await this.get<{ labels: ContactLabel[] }>('/account/contacts/labels', {
      requiresAuth: true
    })
    return res.labels
  }

  /**
   * Sync contacts from Google.
   * Sends the OAuth authorization code obtained from the Google contacts popup
   * to the backend, which exchanges it, calls the People API, and upserts contacts.
   */
  async syncGoogleContacts(code: string, redirectUri: string): Promise<GoogleContactsSyncResponse> {
    return this.post<GoogleContactsSyncResponse>(
      '/google/contacts/sync',
      { code, redirectUri },
      { requiresAuth: true }
    )
  }

  async getVerifiedPhoneNumbers(page = 1, pageSize = 5): Promise<AccountPhoneNumbersResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AccountPhoneNumbersResponse>(`/account/phone-numbers?${params.toString()}`, {
      requiresAuth: true
    })
  }

  async getAllowedVerificationCountries(): Promise<string[]> {
    const res = await this.get<AllowedVerificationCountriesResponse>('/phone-numbers/verify/countries')
    return res.countries
  }

  async triggerPhoneVerification(phoneNumber: string): Promise<PhoneVerificationTriggerResponse> {
    return this.post<PhoneVerificationTriggerResponse>(
      '/phone-numbers/verify',
      { phoneNumber },
      { requiresAuth: true }
    )
  }

  async confirmPhoneVerification(phoneNumber: string, verificationCode: string): Promise<void> {
    const encoded = encodeURIComponent(phoneNumber)
    await this.post<{ success: boolean }>(
      `/phone-numbers/verify/${encoded}/confirm`,
      { verificationCode },
      { requiresAuth: true }
    )
  }

  async removeVerifiedPhoneNumber(phoneNumber: string): Promise<void> {
    const encoded = encodeURIComponent(phoneNumber)
    await this.delete<{ success: boolean }>(
      `/phone-numbers/verify/${encoded}`,
      { requiresAuth: true }
    )
  }

  async getVerificationStatus(phoneNumber: string): Promise<VerificationStatusResponse> {
    const encoded = encodeURIComponent(phoneNumber)
    return this.get<VerificationStatusResponse>(
      `/phone-numbers/verify/${encoded}/status`,
      { requiresAuth: true }
    )
  }

  async getApiKeys(page = 1, pageSize = 5): Promise<ApiKeyListResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<ApiKeyListResponse>(`/account/api-keys?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async createApiKey(name: string): Promise<CreateApiKeyResponse> {
    return this.post<CreateApiKeyResponse>(
      '/account/api-keys',
      { name },
      { requiresAuth: true }
    )
  }

  async getSettings(): Promise<AccountSettingsResponse> {
    return this.get<AccountSettingsResponse>(`/account/settings`, {
      requiresAuth: true,
    })
  }

  async updateSettings(settings: Partial<AccountSettings>): Promise<AccountSettingsResponse> {
    return this.post<AccountSettingsResponse>(
      `/account/settings`,
      { settings },
      { requiresAuth: true }
    )
  }

  async updateCurrency(currency: string): Promise<AccountResponse> {
    return this.patch<AccountResponse>(
      '/account/currency',
      { currency },
      { requiresAuth: true }
    )
  }

  async deleteApiKey(id: number): Promise<void> {
    await this.delete<void>(`/account/api-keys/${id}`, { requiresAuth: true })
  }

  async getAgentVoices(): Promise<AgentVoicesResponse> {
    return this.get<AgentVoicesResponse>('/agent/voices', {
      requiresAuth: false,
    })
  }
}

// Singleton instance
export const accountApiService = new AccountApiService()

// Export both the class and the instance for backward compatibility
export default accountApiService

import { queryOptions } from '@tanstack/react-query'

export const accountQueries = {
  all: ['account'] as const,

  me: () => queryOptions({
    queryKey: [...accountQueries.all, 'me'] as const,
    queryFn: () => accountApiService.getAccount(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  }),

  balance: () => queryOptions({
    queryKey: [...accountQueries.all, 'balance'] as const,
    queryFn: () => accountApiService.getAccountBalance(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Poll every 5s when a top-up is pending (waiting for webhook), otherwise every 60s
    refetchInterval: (query) =>
      (query.state.data?.accountBalance?.pendingAmountCents ?? 0) > 0 ? 5 * 1000 : 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  }),

  referrals: () => queryOptions({
    queryKey: [...accountQueries.all, 'referrals'] as const,
    queryFn: () => accountApiService.getReferrals(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  }),

  settings: () => queryOptions({
    queryKey: [...accountQueries.all, 'settings'] as const,
    queryFn: () => accountApiService.getSettings(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  }),

  agentVoices: () => queryOptions({
    queryKey: ['agent', 'voices'] as const,
    queryFn: () => accountApiService.getAgentVoices(),
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    staleTime: 'static'
  }),

  allowedVerificationCountries: () => queryOptions({
    queryKey: [...accountQueries.all, 'allowed-verification-countries'] as const,
    queryFn: () => accountApiService.getAllowedVerificationCountries(),
    staleTime: Infinity, // country list is static
    refetchOnWindowFocus: false,
  }),

  // Base key prefixes for paginated/infinite queries
  apiKeysBase: ['api-keys'] as const,
  contactsBase: ['contacts'] as const,
  contactsInfiniteBase: ['contacts-infinite'] as const,
  callHistoryBase: ['call-history'] as const,
  callHistoryInfiniteBase: ['call-history-infinite'] as const,
  topUpHistoryBase: ['topup-history'] as const,
  verifiedPhoneNumbersBase: ['verified-phone-numbers'] as const,
  contactsLookupBase: ['contacts-lookup'] as const,
  contactLabelsBase: ['contact-labels'] as const,
}
